<script setup lang="ts">
import type { DataTableHeaders } from '@/plugins/vuetify'
import type MDBBalise from '@/types/balise'
import { notifyError, emptyMongoId } from '@/utils/helpers'

const display = useDisplay()
const storeU = useUtilisateurStore()

const siteCS = 'VGCO'

const isLoading = ref(false)
const filter = ref('')
const balises = ref<MDBBalise[]>([])

const filteredBalises = computed(() => {
  const q = filter.value.trim().toLowerCase()
  if (!q) return balises.value
  return balises.value.filter(b =>
    b.nom.toLowerCase().includes(q) ||
    b.type.toLowerCase().includes(q) ||
    (b.catégorie ?? '').toLowerCase().includes(q))
})

const headers: DataTableHeaders = [
  { title: 'Type', key: 'type', width: '120px' },
  { title: 'Catégorie', key: 'catégorie', width: '160px' },
  { title: 'Sous-catégorie', key: 'sousCatégorie', width: '160px' },
  { title: 'Nom', key: 'nom' },
  { title: 'Changement priorité', key: 'changementPriorité', align: 'end', width: '150px' },
  { title: 'Actions', key: 'actions', sortable: false, align: 'end', width: '80px' },
]

async function getData() {
  isLoading.value = true
  try {
    const res = await apiProphet.get(`sites/${siteCS}/balises`)
    balises.value = res.data as MDBBalise[]
  } catch (e) {
    notifyError(e)
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  getData()
})

// ── Dialog ────────────────────────────────────────────────────────────────
declare interface BaliseForm {
  id: string
  type: string
  catégorieType: string
  catégorie: string
  sousCatégorieType: string
  sousCatégorie: string
  nom: string
  changementPriorité: number
}

const emptyForm = (): BaliseForm => ({
  id: '',
  type: '',
  catégorieType: '',
  catégorie: '',
  sousCatégorieType: '',
  sousCatégorie: '',
  nom: '',
  changementPriorité: 0,
})

const showDialog = ref(false)
const isEditMode = ref(false)
const isSaving = ref(false)
const form = ref<BaliseForm>(emptyForm())

function openCreate() {
  form.value = emptyForm()
  isEditMode.value = false
  showDialog.value = true
}

function openEdit(balise: MDBBalise) {
  form.value = {
    id: balise.id,
    type: balise.type,
    catégorieType: balise.catégorieType ?? '',
    catégorie: balise.catégorie ?? '',
    sousCatégorieType: balise.sousCatégorieType ?? '',
    sousCatégorie: balise.sousCatégorie ?? '',
    nom: balise.nom,
    changementPriorité: balise.changementPriorité,
  }
  isEditMode.value = true
  showDialog.value = true
}

async function saveDialog() {
  if (!form.value.type.trim() || !form.value.nom.trim()) {
    notifyError('Le type et le nom sont requis.')
    return
  }

  isSaving.value = true
  try {
    const siteId = await storeU.getOrFetchSiteId(siteCS)
    const payload: MDBBalise = {
      id: form.value.id || emptyMongoId(),
      siteId,
      type: form.value.type.trim(),
      catégorieType: form.value.catégorieType.trim() || null,
      catégorie: form.value.catégorie.trim() || null,
      sousCatégorieType: form.value.sousCatégorieType.trim() || null,
      sousCatégorie: form.value.sousCatégorie.trim() || null,
      nom: form.value.nom.trim(),
      changementPriorité: form.value.changementPriorité,
    }

    if (isEditMode.value) {
      await apiProphet.put(`sites/${siteCS}/balises/${payload.id}`, payload)
      Notify.success('Balise modifiée avec succès.')
    } else {
      await apiProphet.post(`sites/${siteCS}/balises`, { ...payload, id: '' })
      Notify.success('Balise créée avec succès.')
    }

    showDialog.value = false
    storeU.balises = [] // invalidate cache used elsewhere dans l'app
    await getData()
  } catch (e) {
    notifyError(e)
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <div class="pa-4 pa-sm-6">
    <!-- Toolbar -->
    <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px;">
      <v-text-field
        v-model="filter"
        label="Rechercher"
        density="compact"
        variant="outlined"
        rounded="lg"
        hide-details
        prepend-inner-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"
        clearable
        style="max-width: 260px;"/>

      <v-spacer/>

      <v-btn
        color="primary"
        variant="tonal"
        rounded="pill"
        prepend-icon="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"
        :disabled="isLoading"
        @click="openCreate">
        Nouvelle balise
      </v-btn>
    </div>

    <v-data-table
      :headers="headers"
      :items="filteredBalises"
      :loading="isLoading"
      :hide-default-header="display.smAndDown.value"
      item-value="id"
      density="comfortable"
      hover>

      <!-- Mobile card rows -->
      <template v-if="display.smAndDown.value" #item="{ item }">
        <div class="balise-mobile-card ma-2 pa-3">
          <div class="d-flex align-center justify-space-between mb-2">
            <div style="min-width: 0;">
              <span class="text-subtitle-2 font-weight-bold text-truncate">{{ item.nom }}</span>
              <div class="text-caption text-medium-emphasis text-truncate">{{ item.type || '—' }}</div>
            </div>
            <v-btn icon="M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z" variant="tonal" color="primary" size="small" class="flex-shrink-0 ms-2" @click="openEdit(item)"/>
          </div>
          <v-divider class="mb-2"/>
          <div class="d-flex flex-wrap mt-1" style="gap: 8px;">
            <span class="text-caption">Catégorie: {{ item.catégorie || '—' }}</span>
            <span class="text-caption">Sous-catégorie: {{ item.sousCatégorie || '—' }}</span>
            <span class="text-caption">Priorité: {{ item.changementPriorité }}</span>
          </div>
        </div>
      </template>

      <template #item.actions="{ item }">
        <v-btn icon="M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z" variant="text" size="small" color="primary" @click="openEdit(item)"/>
      </template>

      <template #no-data>
        <div class="text-center py-8 text-medium-emphasis">
          <v-icon icon="M6.5 10C7.3 10 8 9.3 8 8.5S7.3 7 6.5 7 5 7.7 5 8.5 5.7 10 6.5 10M9 6L16 13L11 18L4 11V6H9M9 4H4C2.9 4 2 4.9 2 6V11C2 11.6 2.2 12.1 2.6 12.4L9.6 19.4C9.9 19.8 10.4 20 11 20S12.1 19.8 12.4 19.4L17.4 14.4C17.8 14 18 13.5 18 13C18 12.4 17.8 11.9 17.4 11.6L10.4 4.6C10.1 4.2 9.6 4 9 4M13.5 5.7L14.5 4.7L21.4 11.6C21.8 12 22 12.5 22 13S21.8 14.1 21.4 14.4L16 19.8L15 18.8L20.7 13L13.5 5.7Z" size="40" class="mb-2"/>
          <div>Aucune balise trouvée</div>
        </div>
      </template>
    </v-data-table>

    <!-- ── Create / Edit dialog ──────────────────────────────────────────── -->
    <v-dialog v-model="showDialog" :fullscreen="display.smAndDown.value" :max-width="display.smAndDown.value ? undefined : 560" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center pa-4" style="gap: 10px;">
          <v-avatar color="primary" variant="tonal" size="36" rounded="lg">
            <v-icon :icon="isEditMode ? 'M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z' : 'M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z'" size="20"/>
          </v-avatar>
          <span class="text-h6">{{ isEditMode ? 'Modifier la balise' : 'Ajouter une balise' }}</span>
          <v-spacer/>
          <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" @click="showDialog = false"/>
        </v-card-title>

        <v-divider/>

        <v-card-text class="pa-4">
          <v-container class="pa-0">
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.type" label="Type *" variant="outlined" density="compact" rounded="lg"
                  prepend-inner-icon="M11,13.5V21.5H3V13.5H11M9,15.5H5V19.5H9V15.5M12,2L17.5,11H6.5L12,2M12,5.86L10.08,9H13.92L12,5.86M17.5,13C20,13 22,15 22,17.5C22,20 20,22 17.5,22C15,22 13,20 13,17.5C13,15 15,13 17.5,13M17.5,15A2.5,2.5 0 0,0 15,17.5A2.5,2.5 0 0,0 17.5,20A2.5,2.5 0 0,0 20,17.5A2.5,2.5 0 0,0 17.5,15Z" hide-details="auto"/>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="form.changementPriorité" label="Changement priorité" type="number" variant="outlined"
                  density="compact" rounded="lg" prepend-inner-icon="M14,19H22V17H14V19M14,13.5H22V11.5H14V13.5M14,8H22V6H14V8M2,12.5C2,8.92 4.92,6 8.5,6H9V4L12,7L9,10V8H8.5C6,8 4,10 4,12.5C4,15 6,17 8.5,17H12V19H8.5C4.92,19 2,16.08 2,12.5Z" hide-details="auto"/>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="form.nom" label="Nom *" variant="outlined" density="compact" rounded="lg"
                  prepend-inner-icon="M21.41 11.58L12.41 2.58A2 2 0 0 0 11 2H4A2 2 0 0 0 2 4V11A2 2 0 0 0 2.59 12.42L11.59 21.42A2 2 0 0 0 13 22A2 2 0 0 0 14.41 21.41L21.41 14.41A2 2 0 0 0 22 13A2 2 0 0 0 21.41 11.58M13 20L4 11V4H11L20 13M6.5 5A1.5 1.5 0 1 1 5 6.5A1.5 1.5 0 0 1 6.5 5Z" hide-details="auto"/>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.catégorieType" label="Catégorie type" variant="outlined" density="compact" rounded="lg"
                  prepend-inner-icon="M11 11V2H2V11M4 9V4H9V9M20 6.5C20 7.9 18.9 9 17.5 9S15 7.9 15 6.5 16.11 4 17.5 4 20 5.11 20 6.5M6.5 14L2 22H11M7.58 20H5.42L6.5 18.08M22 6.5C22 4 20 2 17.5 2S13 4 13 6.5 15 11 17.5 11 22 9 22 6.5M19 17V14H17V17H14V19H17V22H19V19H22V17Z" hide-details="auto" clearable/>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.catégorie" label="Catégorie" variant="outlined" density="compact" rounded="lg"
                  prepend-inner-icon="M6.5 10C7.3 10 8 9.3 8 8.5S7.3 7 6.5 7 5 7.7 5 8.5 5.7 10 6.5 10M9 6L16 13L11 18L4 11V6H9M9 4H4C2.9 4 2 4.9 2 6V11C2 11.6 2.2 12.1 2.6 12.4L9.6 19.4C9.9 19.8 10.4 20 11 20S12.1 19.8 12.4 19.4L17.4 14.4C17.8 14 18 13.5 18 13C18 12.4 17.8 11.9 17.4 11.6L10.4 4.6C10.1 4.2 9.6 4 9 4M13.5 5.7L14.5 4.7L21.4 11.6C21.8 12 22 12.5 22 13S21.8 14.1 21.4 14.4L16 19.8L15 18.8L20.7 13L13.5 5.7Z" hide-details="auto" clearable/>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.sousCatégorieType" label="Sous-catégorie type" variant="outlined" density="compact" rounded="lg"
                  prepend-inner-icon="M11 11V2H2V11M4 9V4H9V9M20 6.5C20 7.9 18.9 9 17.5 9S15 7.9 15 6.5 16.11 4 17.5 4 20 5.11 20 6.5M6.5 14L2 22H11M7.58 20H5.42L6.5 18.08M22 6.5C22 4 20 2 17.5 2S13 4 13 6.5 15 11 17.5 11 22 9 22 6.5M19 17V14H17V17H14V19H17V22H19V19H22V17Z" hide-details="auto" clearable/>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.sousCatégorie" label="Sous-catégorie" variant="outlined" density="compact" rounded="lg"
                  prepend-inner-icon="M6.5 10C7.3 10 8 9.3 8 8.5S7.3 7 6.5 7 5 7.7 5 8.5 5.7 10 6.5 10M9 6L16 13L11 18L4 11V6H9M9 4H4C2.9 4 2 4.9 2 6V11C2 11.6 2.2 12.1 2.6 12.4L9.6 19.4C9.9 19.8 10.4 20 11 20S12.1 19.8 12.4 19.4L17.4 14.4C17.8 14 18 13.5 18 13C18 12.4 17.8 11.9 17.4 11.6L10.4 4.6C10.1 4.2 9.6 4 9 4M13.5 5.7L14.5 4.7L21.4 11.6C21.8 12 22 12.5 22 13S21.8 14.1 21.4 14.4L16 19.8L15 18.8L20.7 13L13.5 5.7Z" hide-details="auto" clearable/>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-divider/>

        <v-card-actions class="pa-4">
          <v-spacer/>
          <v-btn variant="text" @click="showDialog = false">Annuler</v-btn>
          <v-btn color="primary" variant="flat" rounded="pill" prepend-icon="M17 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V7L17 3M19 19H5V5H16.17L19 7.83V19M12 12C10.34 12 9 13.34 9 15S10.34 18 12 18 15 16.66 15 15 13.66 12 12 12M6 6H15V10H6V6Z" :loading="isSaving" @click="saveDialog">
            Enregistrer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.balise-mobile-card {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
}
</style>
